import { useState } from 'react';
import ToggleButton from './ToggleButton';
import HotWaterEquipmentField from './HotWaterEquipmentField';
import EquipmentPhotoSection from './EquipmentPhotoSection';

const FAUCET_TYPES = [
    { value: 'hand_wash',      label: 'Lave-main' },
    { value: 'changing_room',  label: 'Salle de bain / Vestiaire' },
    { value: 'technical',      label: 'Technique' },
    { value: 'other',          label: 'Autre' },
];

const ACTIVATION_MODES = [
    { value: 'single_handle', label: 'Mitigeur' },
    { value: 'double_handle', label: 'Double poignée' },
    { value: 'push_button',   label: 'Temporisé' },
    { value: 'sensor',        label: 'Infrarouge' },
    { value: 'pedal',         label: 'Pédale' },
];

function FaucetFields({ data, onChange, imagePreview, onImageChange, onDeleteImage, photoDisabled }) {
    const [showMore, setShowMore] = useState(false);

    const handleToggle = (name) => (e) => onChange({ target: { name, value: e.target.checked } });

    return (
        <div className="equip-fields">

            {/* Nombre */}
            <div className="equip-field">
                <span className="equip-field-label">Nombre d'équipements</span>
                <input
                    type="number"
                    name="nb_equipment"
                    value={data.nb_equipment ?? ''}
                    onChange={onChange}
                    min={1}
                    className="equip-field-num"
                />
            </div>

            {/* Type de robinet */}
            <div className={`equip-field equip-field--col${!data.faucet_type ? ' equip-field--required' : ''}`}>
                <span className="equip-field-label">
                    Type de robinet
                    <span className="equip-field-required-star"> *</span>
                </span>
                <div className="equip-mode-pills">
                    {FAUCET_TYPES.map(t => (
                        <button
                            key={t.value}
                            type="button"
                            className={`equip-mode-pill${data.faucet_type === t.value ? ' active' : ''}`}
                            onClick={() => onChange({ target: { name: 'faucet_type', value: t.value } })}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Débit */}
            <div className="equip-field">
                <span className="equip-field-label">Débit maximal</span>
                <div className="equip-field-compound">
                    <input
                        type="number"
                        name="max_flow"
                        value={data.max_flow ?? ''}
                        onChange={onChange}
                        min={0}
                        className="equip-field-num"
                    />
                    <select name="max_flow_unit" value={data.max_flow_unit || 'L/min'} onChange={onChange} className="equip-field-unit-select">
                        <option value="L/min">L/min</option>
                        <option value="L/cycle">L/cycle</option>
                    </select>
                </div>
            </div>

            {/* Mode d'activation */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Mode d'activation</span>
                <div className="equip-mode-pills">
                    {ACTIVATION_MODES.map(m => (
                        <button
                            key={m.value}
                            type="button"
                            className={`equip-mode-pill${data.activation_mode === m.value ? ' active' : ''}`}
                            onClick={() => onChange({ target: { name: 'activation_mode', value: m.value } })}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Durée minuteur */}
            {data.activation_mode === 'push_button' && (
                <div className={`equip-field${!data.timer_duration ? ' equip-field--required' : ''}`}>
                    <span className="equip-field-label">
                        Durée du minuteur
                        <span className="equip-field-required-star"> *</span>
                    </span>
                    <div className="equip-field-compound">
                        <input
                            type="number"
                            name="timer_duration"
                            value={data.timer_duration ?? ''}
                            onChange={onChange}
                            min={1}
                            required
                            className="equip-field-num"
                        />
                        <span className="equip-field-unit-badge">s</span>
                    </div>
                </div>
            )}

            {/* Eau chaude : raccordement + part typique héritée par les usages */}
            <HotWaterEquipmentField data={data} equipmentType="faucet" onChange={onChange} />

            {/* Photo */}
            <EquipmentPhotoSection imagePreview={imagePreview} onImageChange={onImageChange} onDeleteImage={onDeleteImage} disabled={photoDisabled} />

            {/* Commentaire */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Commentaire</span>
                <input type="text" name="general_comment" value={data.general_comment || ''} onChange={onChange} placeholder="Commentaire général" />
            </div>

            {/* Autres informations */}
            <button
                type="button"
                className={`equip-more-btn${showMore ? ' equip-more-btn--open' : ''}`}
                onClick={() => setShowMore(p => !p)}
            >
                <span>Autres informations</span>
                <svg className="equip-more-chevron" width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>

            {showMore && (
                <div className="equip-more-content">

                    <div className="equip-field">
                        <span className="equip-field-label">Mousseur</span>
                        <ToggleButton name="foamer" value={!!data.foamer} onChange={handleToggle('foamer')} context="equipment" />
                    </div>

                    <div className="equip-field">
                        <span className="equip-field-label">Vanne d'isolement</span>
                        <ToggleButton name="isolation_valve" value={!!data.isolation_valve} onChange={handleToggle('isolation_valve')} context="equipment" />
                    </div>

                    <div className="equip-field equip-field--col">
                        <span className="equip-field-label">Qualité de l'eau en entrée</span>
                        <select name="water_quality_in" value={data.water_quality_in || 'drinkable'} onChange={onChange}>
                            <option value="drinkable">Eau potable</option>
                            <option value="non_drinkable">Eau non potable</option>
                        </select>
                    </div>

                    <div className="equip-field equip-field--col equip-field--last">
                        <span className="equip-field-label">Référence</span>
                        <input type="text" name="reference" value={data.reference || ''} onChange={onChange} placeholder="Référence ou modèle" />
                    </div>

                </div>
            )}
        </div>
    );
}

export default FaucetFields;
